import { useCallback, useMemo, useRef } from 'react';

import { ID } from '../../../../../../../../../types';
import {
  LifestyleDetectionDetectRequestID,
  LifestyleDetectionID
} from '../../../../../../../../lifestyleDetections/lifestyleDetectionsTypes';

import {
  FetchLifestyleDetection,
  LIFESTYLE_DETECTIONS_BY_IMAGE_URL_QUERY
} from '../../../../../../../../lifestyleDetections/queries/lifestyleDetectionsByImageUrl.query';

import { useCacheLifestyleDetections } from '../../../../../../../../lifestyleDetections/hooks/useCacheLifestyleDetections';

import {
  ImagePoint,
  ImageSelectionPayload,
  SingleClickSelectionResolution,
  ThreeDStockContentImageLensProps
} from '../../../../../ThreeDStockContentImageLens';

import { LifestyleDetectionCache } from '../../../../../../../../lifestyleDetections/LifestyleDetectionCache';

interface UseThreeDStockContentSearchInImageDetectionsOptions {
  imageUrl: string;
  imageId?: ID;
  lifestyleId?: ID;
  onSelectedImage?: (
    payload: ImageSelectionPayload,
    detectRequestId: LifestyleDetectionDetectRequestID,
    lifestyleDetectionId?: LifestyleDetectionID
  ) => void;
  onDetectionsChange?: (detections: FetchLifestyleDetection[]) => void;
}

function getLargestDetection(
  lifestyleDetections?: FetchLifestyleDetection[]
): FetchLifestyleDetection | null {
  return (
    lifestyleDetections?.find((detection) => {
      return detection.isLargest;
    }) ?? null
  );
}

function getSmallestContainingDetection(
  lifestyleDetections: FetchLifestyleDetection[],
  point: ImagePoint
): FetchLifestyleDetection | null {
  return lifestyleDetections.reduce<FetchLifestyleDetection | null>(
    (smallestDetection, detection) => {
      const isPointInsideDetection =
        point.x >= detection.x1 &&
        point.x <= detection.x2 &&
        point.y >= detection.y1 &&
        point.y <= detection.y2;

      if (!isPointInsideDetection) {
        return smallestDetection;
      }

      if (!smallestDetection) {
        return detection;
      }

      const smallestArea = smallestDetection.width * smallestDetection.height;
      const currentArea = detection.width * detection.height;

      return currentArea < smallestArea ? detection : smallestDetection;
    },
    null
  );
}

function getDetectionSelection(
  detection: FetchLifestyleDetection
): SingleClickSelectionResolution {
  return {
    selection: {
      x: detection.x1,
      y: detection.y1,
      width: detection.width,
      height: detection.height
    },
    payload: {
      lifestyleDetectionId: detection.id
    }
  };
}

function getInitialSelection(
  detection: FetchLifestyleDetection | null
): ThreeDStockContentImageLensProps['initialSelection'] {
  if (!detection) {
    return undefined;
  }

  return {
    x: detection.x1,
    y: detection.y1,
    width: detection.width,
    height: detection.height,
    largestSearchResult: detection.largestSearchResult
  };
}

function useThreeDStockContentSearchInImageDetections({
  imageUrl,
  imageId,
  lifestyleId,
  onSelectedImage,
  onDetectionsChange
}: UseThreeDStockContentSearchInImageDetectionsOptions) {
  const {
    lifestyleDetections,
    lifestyleDetectionsDetectRequestId,
    lifestyleDetectionsFetched
  } = useCacheLifestyleDetections<FetchLifestyleDetection>({
    imageUrl,
    imageId,
    lifestyleId,
    cacheKey: LifestyleDetectionCache.showCacheKey(imageUrl),
    query: LIFESTYLE_DETECTIONS_BY_IMAGE_URL_QUERY,
    options: {
      enabled: !!imageUrl,
      enabledPlaceholder: false,
      withoutPrefetch: true,
      staleTime: 1000 * 60 * 60,
      keepPreviousData: true,
      onSuccess: (data) => {
        onDetectionsChange?.(
          data?.['lifestyleDetectionsByImageUrl']?.nodes ?? []
        );
      }
    }
  });

  const selectedDetectionRef = useRef<FetchLifestyleDetection | null>(null);

  const largestDetection = useMemo(() => {
    return getLargestDetection(lifestyleDetections);
  }, [lifestyleDetections]);

  const handleResolveSingleClickSelection = useCallback<
    (point: ImagePoint) => SingleClickSelectionResolution | null
  >(
    (point) => {
      if (!lifestyleDetections?.length) {
        selectedDetectionRef.current = null;
        return null;
      }

      const smallestContainingDetection = getSmallestContainingDetection(
        lifestyleDetections,
        point
      );

      if (!smallestContainingDetection) {
        selectedDetectionRef.current = null;
        return null;
      }

      selectedDetectionRef.current = smallestContainingDetection;

      return getDetectionSelection(smallestContainingDetection);
    },
    [lifestyleDetections]
  );

  const handleSelectedImage = useCallback<
    (payload: ImageSelectionPayload) => void
  >(
    (payload) => {
      const selectedDetection = selectedDetectionRef.current;

      selectedDetectionRef.current = null;

      onSelectedImage?.(
        payload,
        lifestyleDetectionsDetectRequestId,
        selectedDetection?.id
      );
    },
    [onSelectedImage, lifestyleDetectionsDetectRequestId]
  );

  return {
    lifestyleDetections,
    initialSelection: getInitialSelection(largestDetection),
    isLoading: !lifestyleDetectionsFetched,
    onResolveSingleClickSelection: handleResolveSingleClickSelection,
    handleSelectedImage
  };
}

export default useThreeDStockContentSearchInImageDetections;
